import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {CircularProgress, IconButton, Tooltip} from '@mui/material';
import {useEffect, useRef, useState} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {useGetAssistantStatusQuery, useSendAssistantMessageMutation} from '../../store/assistant/assistantApi';
import './styles.scss';

const initialMessage = {
    id: 'welcome',
    role: 'assistant',
    text: 'Здравствуйте! Я помогу разобраться с АИС «Успеваемость», отчетами, расписанием и типовыми ошибками. Что вы хотите уточнить?',
    sources: [],
};

const defaultSuggestions = [
    'Как работает эта страница?',
    'Когда у меня следующая пара?',
    'Что делать, если появилась ошибка?',
];

const assistantAvatarUrl = '/assets/assistant.png';

const markdownComponents = {
    a: ({href, children}) => (
        <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    ),
};

export default function AssistantWidget() {
    const token = localStorage.getItem('token');
    const isAuthenticated = token && token !== 'null' && token !== 'undefined';
    const isAuthPage = window.location.pathname.toLowerCase().startsWith('/auth');
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const [conversationId, setConversationId] = useState(null);
    const [messages, setMessages] = useState([initialMessage]);
    const [suggestions, setSuggestions] = useState(defaultSuggestions);
    const [sendMessage, {isLoading}] = useSendAssistantMessageMutation();
    const {data: assistantStatus} = useGetAssistantStatusQuery(undefined, {
        skip: !isAuthenticated || isAuthPage,
        pollingInterval: 60000,
        refetchOnFocus: true,
    });
    const inputRef = useRef(null);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (open) {
            inputRef.current?.focus();
            messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
        }
    }, [open, messages]);

    useEffect(() => {
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, []);

    if (!isAuthenticated || isAuthPage || assistantStatus?.enabled !== true) return null;

    const clearConversation = () => {
        setConversationId(null);
        setMessages([initialMessage]);
        setSuggestions(defaultSuggestions);
        setDraft('');
    };

    const submit = async (text = draft) => {
        const normalized = text.trim();
        if (!normalized || isLoading) return;

        const userMessage = {id: `user-${Date.now()}`, role: 'user', text: normalized, sources: []};
        setMessages((current) => [...current, userMessage]);
        setDraft('');

        try {
            const response = await sendMessage({
                conversationId,
                message: normalized,
                pageContext: {
                    path: window.location.pathname,
                    title: document.title,
                },
            }).unwrap();

            setConversationId(response.conversationId);
            setSuggestions(response.suggestedQuestions?.length ? response.suggestedQuestions : defaultSuggestions);
            setMessages((current) => [...current, {
                id: `assistant-${Date.now()}`,
                role: 'assistant',
                text: response.message || 'Не удалось сформировать ответ.',
                sources: response.sources || [],
                isError: response.success === false,
            }]);
        } catch (error) {
            const status = error?.status;
            const text = status === 401
                ? 'Срок авторизации истек. Войдите в систему повторно.'
                : 'Помощник временно недоступен. Попробуйте немного позже.';
            setMessages((current) => [...current, {
                id: `assistant-error-${Date.now()}`,
                role: 'assistant',
                text,
                sources: [],
                isError: true,
            }]);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submit();
        }
    };

    return (
        <div className="assistant-widget">
            {open && (
                <section
                    className="assistant-panel"
                    role="dialog"
                    aria-label="ИИ-ассистент Елена"
                    aria-live="polite"
                >
                    <header className="assistant-header">
                        <div className="assistant-header__identity">
                            <img className="assistant-avatar" src={assistantAvatarUrl} alt=""/>
                            <div>
                                <strong>ИИ-ассистент Елена</strong>
                                <span>АИС «Успеваемость»</span>
                            </div>
                        </div>
                        <div className="assistant-header__actions">
                            <Tooltip title="Очистить диалог">
                                <IconButton onClick={clearConversation} aria-label="Очистить диалог" size="small">
                                    <DeleteOutlineRoundedIcon/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Закрыть">
                                <IconButton onClick={() => setOpen(false)} aria-label="Закрыть помощника" size="small">
                                    <CloseRoundedIcon/>
                                </IconButton>
                            </Tooltip>
                        </div>
                    </header>

                    <div className="assistant-messages">
                        {messages.map((message) => (
                            <article
                                key={message.id}
                                className={`assistant-message assistant-message--${message.role}${message.isError ? ' assistant-message--error' : ''}`}
                            >
                                {message.role === 'assistant' ? (
                                    <div className="assistant-markdown">
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            components={markdownComponents}
                                            skipHtml
                                        >
                                            {message.text}
                                        </ReactMarkdown>
                                    </div>
                                ) : <div>{message.text}</div>}
                                {message.sources?.length > 0 && (
                                    <details className="assistant-sources">
                                        <summary>Откуда получен ответ</summary>
                                        <ul>
                                            {message.sources.map((source) => <li key={source}>{source}</li>)}
                                        </ul>
                                    </details>
                                )}
                            </article>
                        ))}
                        {isLoading && (
                            <div className="assistant-loading" aria-label="Помощник готовит ответ">
                                <CircularProgress size={18}/>
                                <span>Готовлю ответ…</span>
                            </div>
                        )}
                        <div ref={messagesEndRef}/>
                    </div>

                    <div className="assistant-suggestions" aria-label="Примеры вопросов">
                        {suggestions.slice(0, 3).map((suggestion) => (
                            <button key={suggestion} type="button" onClick={() => submit(suggestion)} disabled={isLoading}>
                                {suggestion}
                            </button>
                        ))}
                    </div>

                    <div className="assistant-composer">
                        <textarea
                            ref={inputRef}
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Напишите вопрос…"
                            aria-label="Вопрос помощнику"
                            maxLength={2000}
                            rows={2}
                        />
                        <button
                            type="button"
                            className="assistant-send"
                            onClick={() => submit()}
                            disabled={!draft.trim() || isLoading}
                            aria-label="Отправить вопрос"
                        >
                            {isLoading ? <CircularProgress size={20}/> : <SendRoundedIcon/>}
                        </button>
                    </div>
                    <p className="assistant-disclaimer">Ответы помощника могут содержать неточности. Важные решения сверяйте с регламентами.</p>
                </section>
            )}

            <button
                type="button"
                className={`assistant-launcher${open ? ' assistant-launcher--open' : ''}`}
                onClick={() => setOpen((value) => !value)}
                aria-label={open ? 'Закрыть ИИ-ассистента Елену' : 'Открыть ИИ-ассистента Елену'}
                aria-expanded={open}
            >
                {open ? <CloseRoundedIcon/> : <img className="assistant-launcher__avatar" src={assistantAvatarUrl} alt=""/>}
                <span>{open ? 'Закрыть' : 'ИИ-ассистент'}</span>
            </button>
        </div>
    );
}
